import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Button, Menu } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import styled from "styled-components";
import { ScreenReader } from "@/App/components";
import { ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { MemberEligibilityInfo } from "@/App/types";
import { ThemedMenuButton } from "../../themed";

interface MobileMainBarProps {
  onMobileMenuToggle: () => void;
  mobileMenuIcon: "menu-bars" | "x";
}

const MobileHamburgerButton = styled(Button)`
  path {
    fill: #313e48 !important;
  }
`;

const EligibilityBarContainer = styled(Shelf)`
  background-color: white;
  border-top: solid 1px #cecece;
`;

const EligibilitySelectionMenuButton = styled(ThemedMenuButton)`
  * {
    text-transform: none;
    font-weight: normal;
    letter-spacing: 0 !important;
    color: #313e48;
  }

  path {
    fill: #313e48 !important;
  }
`;

const MobileMenuBarContainer = styled(Shelf)`
  background-color: white;
  height: 4.375rem !important;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  padding-top: 8px;
`;

export const MobileMainBar = ({
  mobileMenuIcon,
  onMobileMenuToggle,
}: MobileMainBarProps) => {
  const { t: translate } = useTranslation();
  const navigateClient = useNavigateClient();
  const { experience, overrideLandingPage } = useContext(ExperienceContext);
  const { memberEligibility, availableEligibility, setActiveMemberEligibility } = useContext(MemberEligibilityContext);

  const onLogoClicked = useCallback(() => {
    navigateClient(overrideLandingPage ?? "/");
  }, [overrideLandingPage]);

  const onEligibilitySelected = useCallback(
    (elig: MemberEligibilityInfo) => {
      setActiveMemberEligibility(elig);
    },
    [availableEligibility],
  );

  const getEligibilityKey = (elig: MemberEligibilityInfo) => {
    return `${elig.plan.client_id}-${elig.member.external_member_id}-${elig.member.person_code}`;
  };

  return (
    <>
      <Stack spacing="none">
        <MobileMenuBarContainer>
          <ScreenReader>{translate("MENU_BAR.home")}</ScreenReader>
          <img
            style={{ cursor: "pointer" }}
            data-testid="img-logo"
            onClick={onLogoClicked}
            height={30}
            src={experience?.theme?.logo_desktop}
            alt="Client Logo"
          />
          <Shelf.Spacer />
          <MobileHamburgerButton appearance="text" icon={mobileMenuIcon} onClick={onMobileMenuToggle}></MobileHamburgerButton>
        </MobileMenuBarContainer>
        {availableEligibility.length > 1 && (
          <EligibilityBarContainer>
            <Shelf.Spacer />
            <EligibilitySelectionMenuButton
              dynamicCaret={true}
              label={
                <>
                  {translate("MENU_BAR.viewingAs")} {memberEligibility?.member?.first_name} {memberEligibility?.member?.last_name}
                </>
              }
              appearance="text"
            >
              {availableEligibility.map((elig) => {
                return (
                  <Menu.Item onClick={() => onEligibilitySelected(elig)} key={getEligibilityKey(elig)}>
                    <>
                      {elig.member.first_name} {elig.member.last_name}
                    </>
                  </Menu.Item>
                );
              })}
            </EligibilitySelectionMenuButton>
            <Shelf.Spacer />
          </EligibilityBarContainer>
        )}
      </Stack>
    </>
  );
};
